import * as d3 from 'd3';
import { useUserPreferences } from 'hooks/useUserPreferences';
import { memo } from 'react';
import { withContentRect } from 'react-measure';
import colors, { LRMCustomDotsColors } from 'styles/colors';
import { guesstimateNumberLabelWidthBasedOnConst } from 'utils/d3Helper';
import { FormatterType } from 'utils/formatters';
import { formatAmountBasedOnLocale } from 'utils/unitConverters';

interface BarColumnCellProps {
  value: number;
  category: string;
  data: { COUNT: number }[];
  isLastRow: boolean;
  barFillColor?: string;
  labelsFormatter: string;
  suffix?: string | null;
}

function BarColumnCell({
  value,
  category,
  data,
  isLastRow,
  barFillColor,
  labelsFormatter,
  suffix = '',
}: BarColumnCellProps) {
  const barValues = value !== 0 ? value : '';
  const hasValue = value !== 0;
  const hasSuffix = suffix && String(suffix).length > 0;

  const { maxDataVal } = data.reduce(
    (agg, { COUNT: val }) => {
      val = Math.abs(val);
      agg.maxDataVal = agg.maxDataVal < val ? val : agg.maxDataVal;
      agg.minDataVal = agg.minDataVal > val ? val : agg.minDataVal;
      return agg;
    },
    { maxDataVal: data.length > 0 ? -Infinity : 0, minDataVal: data.length > 0 ? Infinity : 0 }
  );

  const color = barFillColor ? barFillColor : LRMCustomDotsColors[category];

  const decimals = labelsFormatter === FormatterType.FLOAT_TWO_DECIMAL ? 2 : 0;
  const preferences = useUserPreferences();
  const textFormatted = formatAmountBasedOnLocale(barValues ?? 0, decimals, preferences.locale);
  const textValue = hasValue && hasSuffix ? `${textFormatted} ${String(suffix)}` : textFormatted;

  const BarComponentMeasured = withContentRect('bounds')(({ measureRef, contentRect }) => {
    let { bounds: { width } = { width: 0 } } = contentRect;
    width ??= 0;
    const totalBarWidth = width;

    const xScale = d3
      .scaleLinear()
      .domain([0, maxDataVal > 0 ? maxDataVal : 1])
      .range([0, totalBarWidth]);

    const barWidth = xScale(Math.abs(value));

    const textSizeGuesstimate = guesstimateNumberLabelWidthBasedOnConst(textValue, 9);
    const isTextLabelTooBigWithBar =
      textSizeGuesstimate + barWidth + 10 > totalBarWidth ? true : false;
    const isTextAndBarBiggerThanWidth =
      isTextLabelTooBigWithBar && textSizeGuesstimate > barWidth ? true : false;

    const textX = isTextLabelTooBigWithBar
      ? barWidth - textSizeGuesstimate + textSizeGuesstimate / 3 - 5
      : barWidth + 5;
    const textColor = isTextLabelTooBigWithBar ? colors.white : colors.black;

    return (
      <div id="bar-div">
        <svg id="bar-wrapper" ref={measureRef} height={20} width="100%">
          <g id="bar-bounds">
            <g id="chart-group" fill={color}>
              <rect x="0" y="0" width={barWidth} height="20" rx="4" ry="4" />
              <text x={textX} y="14" fill={textColor} fontSize="1rem">
                {isTextAndBarBiggerThanWidth ? '' : textValue}
              </text>
            </g>
          </g>
        </svg>
      </div>
    );
  });

  return <>{!isLastRow && <BarComponentMeasured />}</>;
}

export default memo(BarColumnCell);
